const getArray = (array) => (Array.isArray(array) ? array : []);

class MultiThresholdGiftManager {
  constructor(opts) {
    this.config = opts.config ? { thresholds: opts.config.conditions } : { thresholds: [] };
    this.mode = opts.config.highest_threshold_only ? 'highestOnly' : 'cumulative';
    this.debounceMs = 100;

    this._cartValue = 0;
    this._discountCode = null;

    this._desiredGiftVariantIds = new Set();
    this._activeGiftVariantIds = new Set();
    this._timer = null;
    this._busy = false;

    this.getGiftParams = {
      item_gifts_with_condition: (content) => ({ _source: 'YK', _yk_source: 'YK', _widget_id: `_gift_2_plus_1_${content}` }),
      gifts_with_threshold: (content) => ({ _source: 'YK', _yk_source: 'YK', _widget_id: `_gift_amount_${content}` }),
      item_deletable_gift: (content) => ({ _source: 'YK', _yk_source: 'YK', _widget_id: `_deletable_gift_${content}` }),
    };

    this.sortThresholds();
    this.bootstrapStateFromRebuy();
  }

  get cartValue() {
    return this._cartValue;
  }
  set cartValue(computed) {
    if (computed === this._cartValue) return;
    this._cartValue = computed;
    this._scheduleApply();
  }

  get discountCode() {
    return this._discountCode;
  }
  set discountCode(computed) {
    if (computed === this._discountCode) return;
    this._discountCode = computed;
    this._scheduleApply();
  }

  sortThresholds() {
    if (Array.isArray(this.config.thresholds)) {
      this.config.thresholds.sort((a, b) => this._getThresholdSortValue(a) - this._getThresholdSortValue(b));
    }
  }

  _getThresholdSortValue(threshold) {
    switch (threshold?.type) {
      case 'item_gifts_with_condition':
        return Number(threshold.required_quantity || 0);
      case 'item_deletable_gift':
        return Number(threshold.all_products_qty_threshold || 0);
      default:
        return Number(threshold.amount || 0);
    }
  }

  bootstrapStateFromRebuy() {
    this._syncActiveGiftVariantIds();
  }

  addToCart(id, quantity = 1, gift) {
    const threshold = this.config.thresholds.find((t) =>
      t.gifts.flatMap((g) => g.product.variants.map((v) => v.id)).includes(id)
    );

    const getExtraParams = this.getGiftParams[threshold?.type];
    const extraGiftParams =
      threshold?.type === 'item_deletable_gift'
        ? getExtraParams?.(threshold.all_products_qty_threshold)
        : threshold?.amount
          ? getExtraParams?.(threshold.amount)
          : threshold?.required_product_id && threshold?.required_quantity
            ? getExtraParams?.(threshold.required_product_id)
            : undefined;

    const redirectProps = {
      _redirectTo: gift.redirect.handle ? `/products/${gift.redirect.handle}` : undefined,
    };

    Rebuy.Cart.addItem({
      id,
      quantity,
      properties: {
        ...(!!threshold?.amount && {
          [THRESHOLD_ATTRIBUTE_KEY]: threshold.amount + ' zł',
        }),
        ...(!!threshold?.required_product_id &&
          !!threshold.required_quantity && {
            [THRESHOLD_REQUIRED_PRODUCT_KEY]: threshold.required_product_id,
            [THRESHOLD_REQUIRED_QUANTITY_KEY]: threshold.required_quantity,
          }),
        ...(extraGiftParams || {}),
        ...(!!gift.redirect && redirectProps),
      },
    });
  }

  removeGifts(variantIds = []) {
    if (variantIds.length === 0) return;
    const items = Rebuy.SmartCart.cart.items ?? [];
    const lineItems = items.filter((item) => variantIds.includes(item.id));
    let updates = {};

    lineItems.forEach((item) => (updates[item.id] = 0));
    Rebuy.Cart.removeItems(updates);
  }

  _scheduleApply() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this._apply().catch(console.error);
    }, this.debounceMs);
  }

  _updateWidgetGifts(gifts) {
    const elements = document.querySelectorAll('.mtg-product-wrapper[data-prod-id]') ?? [];
    elements.forEach((el) => {
      const prodId = el.getAttribute('data-prod-id');
      const giftProductIds = [];
      for (let key of Object.keys(gifts)) {
        const gift = gifts[key];
        giftProductIds.push(String(gift.product.id));
      }
      if (giftProductIds.includes(prodId)) {
        el.classList.add('mtg-product-wrapper--gift');
      } else {
        el.classList.remove('mtg-product-wrapper--gift');
      }
    });
  }

  _resolveVariantId(gift) {
    const variants = gift && gift.product && getArray(gift.product.variants);
    if (!variants.length) return null;
    return variants[0].id || null;
  }

  _getThresholdGiftVariantIds(threshold) {
    return getArray(threshold?.gifts)
      .flatMap((gift) => getArray(gift?.product?.variants).map((variant) => Number(variant.id)))
      .filter(Boolean);
  }

  _getLineItemProperty(item, key) {
    const properties = item?.properties;
    if (!properties) return undefined;

    if (Array.isArray(properties)) {
      return properties.find((property) => property?.name === key || property?.key === key)?.value;
    }

    return properties[key];
  }

  _isMultiThresholdGiftItem(item) {
    const widgetId = String(this._getLineItemProperty(item, '_widget_id') || '');

    return (
      widgetId.startsWith('_gift_2_plus_1_') ||
      widgetId.startsWith('_gift_amount_') ||
      widgetId.startsWith('_deletable_gift_')
    );
  }

  _syncActiveGiftVariantIds(items = Rebuy.SmartCart?.cart?.items ?? []) {
    this._activeGiftVariantIds = new Set(
      items
        .filter((item) => this._isMultiThresholdGiftItem(item))
        .map((item) => Number(item.variant_id || item.id))
        .filter(Boolean)
    );
  }

  _calculateDigitalProductsTotalValue() {
    const cartItems = Rebuy.SmartCart?.cart?.items || [];
    let digitalProductsTotalValue = 0;

    const digitalProductsArr = cartItems?.filter((item) => {
      return item?.product?.tags?.includes('digital');
    });

    digitalProductsArr.forEach((item) => {
      digitalProductsTotalValue += item.original_price * item.quantity;
    });

    return digitalProductsTotalValue;
  }

  getDiscountCodeArrFromCartItems() {
    if (!Array.isArray(Rebuy.SmartCart.cart.items) || Rebuy.SmartCart.cart.items.length === 0) return null;

    function onlyUnique(value, index, array) {
      return array.indexOf(value) === index;
    }

    const arr = Rebuy.SmartCart.cart.items.flatMap(({ discounts }) => discounts.map(({ title }) => title));
    return arr.filter(onlyUnique);
  }

  resetDeletedGifts() {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('yk_gift_deleted:')) {
        localStorage.removeItem(key);
      }
    });
  }

  _wasGiftDeleted(id) {
    return !!localStorage.getItem(`yk_gift_deleted:${id}`);
  }

  markGiftAsDeleted(id) {
    localStorage.setItem(`yk_gift_deleted:${id}`, 'true');
  }

  _clearGiftDeletion(id) {
    localStorage.removeItem(`yk_gift_deleted:${id}`);
  }

  _clearThresholdGiftDeletions(threshold) {
    this._getThresholdGiftVariantIds(threshold).forEach((id) => this._clearGiftDeletion(id));
  }

  _isThresholdReached(threshold, value, cartProducts = []) {
    const nonGiftCartProducts = cartProducts.filter((product) => !product.is_gift);
    const allProductsQty = nonGiftCartProducts.reduce((sum, product) => sum + Number(product.quantity || 0), 0);

    switch (threshold?.type) {
      case 'item_gifts_with_condition':
        return nonGiftCartProducts.find(
          (product) => product.id == threshold?.required_product_id && product.quantity >= threshold?.required_quantity
        );
      case 'gifts_with_threshold':
        return Number(value) >= Number(threshold.amount || 0);
      case 'item_deletable_gift':
        return allProductsQty >= Number(threshold.all_products_qty_threshold || 1);
      default:
        return false;
    }
  }

  _computeDesiredGifts(value, cartProducts = []) {
    const giftsList = {};
    const code = this.discountCode;
    const currentDiscountCode = code ? String(code).toLowerCase().trim() : null;
    const desired = new Set();
    const thresholds = getArray(this.config.thresholds);

    const reached = thresholds.filter((t) => {
      const isReached = this._isThresholdReached(t, value, cartProducts);

      if (!isReached && t.type === 'item_deletable_gift') {
        this._clearThresholdGiftDeletions(t);
      }

      return isReached;
    });

    console.log('-----------giftsList', giftsList);

    if (!reached.length) return [desired, giftsList];

    const tiers = this.mode === 'highestOnly' ? [reached[reached.length - 1]] : reached;
    tiers.forEach((tier) => {
      const discountCode = tier.discount_code ? atob(tier.discount_code).toLowerCase().trim() : null;
      if (discountCode && discountCode !== currentDiscountCode) return;

      const gifts = getArray(tier.gifts);
      gifts.forEach((gift) => {
        const vid = this._resolveVariantId(gift);
        if (tier.type === 'item_deletable_gift' && this._wasGiftDeleted(vid)) return;
        if (vid) {
          desired.add(vid);
          giftsList[vid] = gift;
        }
      });
    });

    console.log('-----------giftsList', giftsList);

    return [desired, giftsList];
  }

  onceEvent(type, target = document, timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
      const on = (e) => {
        cleanup();
        resolve(e.detail);
      };
      const to = setTimeout(() => {
        cleanup();
        reject(new Error(`Timeout waiting for ${type}`));
      }, timeoutMs);
      const cleanup = () => {
        clearTimeout(to);
        target.removeEventListener(type, on, opts);
      };
      const opts = { once: true };
      target.addEventListener(type, on, opts);
    });
  }

  async addToCartSequential(callback = () => {}) {
    const done = this.onceEvent('rebuy:cart.change');
    callback();
    const detail = await done;
    await new Promise((r) => setTimeout(r, 150));
    return detail;
  }

  getGiftEligibleQuantity(variantId, cartProducts) {
    let variantGift = null;
    const threshold = this.config.thresholds?.find((threshold) => {
      if (threshold.type !== 'item_gifts_with_condition') return false;
      const gift = threshold.gifts?.find((g) => getArray(g.product.variants).some((v) => v.id === variantId));
      if (!gift) return false;
      variantGift = gift;
      return true;
    });

    const { required_quantity, required_product_id } = threshold || {};
    const currentGiftQty = cartProducts.find((p) => p.variant_id === variantId)?.quantity || 0;

    if (!required_quantity || !required_product_id) {
      return { eligibleGiftQty: 1, currentGiftQty, missingQuantity: 0 };
    }

    const requiredProductQty = cartProducts.find((p) => p.id === required_product_id)?.quantity || 0;
    const eligibleGiftQty = Math.max(1, Math.floor(requiredProductQty / required_quantity));
    const missingQuantity = eligibleGiftQty - currentGiftQty;

    return { eligibleGiftQty, currentGiftQty, missingQuantity };
  }

  updateGiftQuantity(variantId, quantity) {
    const lineItem = Rebuy.SmartCart.cart.items.find((i) => i.variant_id === variantId);
    if (!lineItem) return;

    return new Promise((resolve, reject) => {
      Rebuy.Cart.changeItem(
        {
          id: lineItem.key,
          quantity,
        },
        {
          success: resolve,
          error: reject,
        }
      );
    });
  }

  async _apply() {
    if (this._busy) return;
    console.log('---- apply() was triggered -----');
    this._busy = true;
    let giftsData = [];

    try {
      const digitalProductsTotalValue = this._calculateDigitalProductsTotalValue();
      const physicalProductsTotalValue = this.cartValue - digitalProductsTotalValue / 100;
      const cartItems = Rebuy.SmartCart.cart.items ?? [];
      this._syncActiveGiftVariantIds(cartItems);
      const cartProducts = cartItems.map((i) => ({
        id: i.product_id,
        quantity: i.quantity,
        variant_id: i.variant_id,
        is_gift: (i.product?.tags || []).includes('gratis_product'),
      }));
      const [desired, gifts] = this._computeDesiredGifts(physicalProductsTotalValue, cartProducts);
      this._desiredGiftVariantIds = desired;
      giftsData = gifts;

      const toRemove = [...this._activeGiftVariantIds].filter((id) => !this._desiredGiftVariantIds.has(id));
      if (window.Shopify.theme.name !== 'MASTER') console.log('toRemove', toRemove);

      // for debug
      let toAdjust = [];
      for (const variantId of this._activeGiftVariantIds) {
        const { missingQuantity, eligibleGiftQty, currentGiftQty } = this.getGiftEligibleQuantity(
          variantId,
          cartProducts
        );
        if (missingQuantity !== 0 || eligibleGiftQty !== currentGiftQty) {
          await this.updateGiftQuantity(variantId, eligibleGiftQty);
          toAdjust.push(variantId);
        } else if (missingQuantity < 0) {
          toRemove.push(variantId);
        }
      }
      if (window.Shopify.theme.name !== 'MASTER') console.log('toAdjust', toAdjust);

      try {
        this.removeGifts(toRemove);
        toRemove.forEach((id) => this._activeGiftVariantIds.delete(id));
      } catch (e) {
        console.error('removeGifts error', e);
      }

      const toAdd = [...this._desiredGiftVariantIds].filter((id) => !this._activeGiftVariantIds.has(id));
      if (window.Shopify.theme.name !== 'MASTER') console.log('toAdd', toAdd);

      for (const variantId of toAdd) {
        try {
          await this.addToCartSequential(() => this.addToCart(variantId, 1, gifts[variantId]));
          this._activeGiftVariantIds.add(variantId);
        } catch (e) {
          console.error('addToCart error', e);
        }
      }
    } finally {
      this._busy = false;
      this._updateWidgetGifts(giftsData);
    }
  }
}

// ---- Inicjalizacja ----
document.addEventListener('rebuy:smartcart.ready', () => {
  const el = document.getElementById('SmartCartJson');
  let config = null;

  if (el) {
    try {
      const parsed = JSON.parse(el.innerText || el.textContent || '{}');
      config = parsed && parsed.multi_threshold_gifts ? parsed.multi_threshold_gifts : null;
    } catch (err) {
      console.error('Nie udało się sparsować #SmartCartJson:', err);
    }
  }

  if (!config) {
    console.warn('Brak configu: SmartCartJson.multi_threshold_gifts');
    return;
  }

  window.ykMTGManager = new MultiThresholdGiftManager({ config, mode: 'cumulative' });

  setTimeout(() => {
    const codeArray = window.ykMTGManager.getDiscountCodeArrFromCartItems();
    window.ykMTGManager.discountCode = codeArray?.length > 0 ? codeArray[0] : Rebuy.Cart.discount;
    window.ykMTGManager.cartValue = Rebuy.SmartCart.cart.original_total_price / 100;
  }, 150);

  document.addEventListener('rebuy:cart.change', () => {
    setTimeout(() => {
      const codeArray = window.ykMTGManager.getDiscountCodeArrFromCartItems();
      window.ykMTGManager.discountCode = codeArray?.length > 0 ? codeArray[0] : Rebuy.Cart.discount;
      window.ykMTGManager.cartValue = Rebuy.SmartCart.cart.original_total_price / 100;
    }, 150);
  });
});

// helper for awaiting rebuy addItem
(function hookCartAddFetch() {
  if (window.__cartAddHooked) return;
  window.__cartAddHooked = true;

  const origFetch = window.fetch;
  window.fetch = async function (...args) {
    const [input] = args;
    const url = typeof input === 'string' ? input : input.url;

    const isCartAdd = /\/cart\/add\.js(\?|$)/.test(url);
    const res = await origFetch.apply(this, args);

    if (isCartAdd) {
      res
        .clone()
        .json()
        .then((data) => {
          window.dispatchEvent(new CustomEvent('cart:add:done', { detail: data }));
        })
        .catch(() => {});
    }
    return res;
  };
})();
